/**
 * Recent Questions Collection
 *
 * Handles API operations for collections of recent questions
 * Extends the base List<T> class following warbler pattern
 * Provides filtering, search, and analytics functionality
 */

import { get, post } from '@/services/http';
import { RecentQuestionModel } from '@/models/recent_question';
import {
  IFetchRecentQuestionsRequest,
  IQuestionExecutionStats,
  QuestionCategory,
  QuestionPriority,
} from '@/models/insights/';
import List from './list';

export class RecentQuestionsList extends List<RecentQuestionModel> {
  public total: number = 0;
  public pagination: any = null;

  /**
   * Fetch recent questions from API with filtering and pagination
   */
  public async fetch(options: IFetchRecentQuestionsRequest = {}): Promise<any> {
    try {
      const queryParams = {
        limit: options.limit || 20,
        offset: options.offset || 0,
        sort_by: options.sort_by || 'last_executed_at',
        sort_order: options.sort_order || 'desc',
        category: options.category,
        search: options.search,
        is_pinned: options.is_pinned,
        date_from: options.date_from,
        date_to: options.date_to,
        min_execution_count: options.min_execution_count,
        max_execution_count: options.max_execution_count,
        include_metadata: options.include_metadata || false,
      };

      // Remove undefined values
      Object.keys(queryParams).forEach((key) =>
        queryParams[key as keyof typeof queryParams] === undefined && delete queryParams[key as keyof typeof queryParams],
      );

      const response: any = await get('/recent-questions', queryParams);

      if (response && response.success) {
        this.mapData(response);
        return response;
      }
      throw new Error(response?.message || 'Failed to fetch recent questions');
    } catch (error) {
      console.error('Error fetching recent questions:', error);
      throw error;
    }
  }

  /**
   * Map API response data to collection items
   */
  public mapData(response: any): void {
    // Debug: Log the raw API response
    console.log('🔍 [DEBUG] RecentQuestionsList.mapData raw response:', {
      success: response.success,
      hasData: !!response.data,
      dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
      itemCount: Array.isArray(response.data) ? response.data.length : 0,
      firstItem: Array.isArray(response.data) && response.data.length > 0 ? {
        id: response.data[0].id,
        question_text: response.data[0].question_text,
        measure_code: response.data[0].measure_code,
        dimension_code: response.data[0].dimension_code,
        hasMeasureCode: !!response.data[0].measure_code,
        hasDimensionCode: !!response.data[0].dimension_code,
      } : null,
    });

    // Clear existing items
    this.items = [];
    this.total = response.pagination?.total || 0;
    this.pagination = response.pagination || null;

    if (response.data && Array.isArray(response.data)) {
      for (const questionData of response.data) {
        // Debug: Log each question data before creating the model
        console.log('🔍 [DEBUG] Creating RecentQuestionModel with data:', {
          id: questionData.id,
          question_text: questionData.question_text,
          measure_code: questionData.measure_code,
          dimension_code: questionData.dimension_code,
          measureCodeType: typeof questionData.measure_code,
          dimensionCodeType: typeof questionData.dimension_code,
        });

        const questionModel = new RecentQuestionModel(questionData);

        // Debug: Log the created model
        console.log('🔍 [DEBUG] Created RecentQuestionModel:', {
          id: questionModel.id,
          question_text: questionModel.question_text,
          measure_code: questionModel.measure_code,
          dimension_code: questionModel.dimension_code,
          measureCodeType: typeof questionModel.measure_code,
          dimensionCodeType: typeof questionModel.dimension_code,
        });

        this.add(questionModel);
      }
    }
  }

  /**
   * Search questions by text
   */
  public async search(searchText: string, options: Partial<IFetchRecentQuestionsRequest> = {}): Promise<any> {
    return this.fetch({
      ...options,
      search: searchText,
    });
  }

  /**
   * Filter questions by category
   */
  public async filterByCategory(category: QuestionCategory | string, options: Partial<IFetchRecentQuestionsRequest> = {}): Promise<any> {
    return this.fetch({
      ...options,
      category,
    });
  }

  /**
   * Get questions by execution frequency
   */
  public async getByExecutionCount(minCount: number, maxCount?: number): Promise<any> {
    return this.fetch({
      min_execution_count: minCount,
      max_execution_count: maxCount,
    });
  }

  /**
   * Get recent questions by date range
   */
  public async getByDateRange(dateFrom: string, dateTo: string): Promise<any> {
    return this.fetch({
      date_from: dateFrom,
      date_to: dateTo,
    });
  }

  /**
   * Get only pinned questions
   */
  public async getPinnedQuestions(): Promise<any> {
    return this.fetch({
      is_pinned: true,
      sort_by: 'last_executed_at',
      sort_order: 'desc',
    });
  }

  /**
   * Get only unpinned questions
   */
  public async getUnpinnedQuestions(): Promise<any> {
    return this.fetch({
      is_pinned: false,
    });
  }

  /**
   * Get questions with highest execution counts
   */
  public async getMostExecuted(limit: number = 10): Promise<any> {
    return this.fetch({
      sort_by: 'execution_count',
      sort_order: 'desc',
      limit,
    });
  }

  /**
   * Get recently created questions
   */
  public async getRecentlyCreated(limit: number = 10): Promise<any> {
    return this.fetch({
      sort_by: 'created_at',
      sort_order: 'desc',
      limit,
    });
  }

  /**
   * Get questions execution statistics
   */
  public async getExecutionStats(): Promise<IQuestionExecutionStats> {
    try {
      const response: any = await get('/recent-questions/stats');
      if (response.data && response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data?.message || 'Failed to fetch stats');
    } catch (error) {
      console.error('Error fetching execution stats:', error);
      throw error;
    }
  }

  /**
   * Get available question categories
   */
  public async getCategories(): Promise<string[]> {
    try {
      const response: any = await get('/recent-questions/categories');
      if (response.data && response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data?.message || 'Failed to fetch categories');
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Bulk pin questions
   */
  public async bulkPin(questionIds: number[], pinReason: string = 'frequently_used'): Promise<void> {
    try {
      await post('/recent-questions/bulk-pin', {
        question_ids: questionIds,
        pin_reason: pinReason,
      });
    } catch (error) {
      console.error('Error bulk pinning questions:', error);
      throw error;
    }
  }

  /**
   * Bulk delete questions
   */
  public async bulkDelete(questionIds: number[]): Promise<void> {
    try {
      await post('/recent-questions/bulk-delete', {
        question_ids: questionIds,
      });
    } catch (error) {
      console.error('Error bulk deleting questions:', error);
      throw error;
    }
  }

  /**
   * Get questions grouped by category
   */
  public getGroupedByCategory(): Record<string, RecentQuestionModel[]> {
    const grouped: Record<string, RecentQuestionModel[]> = {};

    for (const question of this.items) {
      const category = (question as any).getCategoryFromMetadata();
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(question);
    }

    return grouped;
  }

  /**
   * Filter local items by category
   */
  public filterItemsByCategory(category: QuestionCategory | string): RecentQuestionModel[] {
    return this.items.filter((question) => (question as any).getCategoryFromMetadata() === category);
  }

  /**
   * Filter local items by execution count
   */
  public filterItemsByExecutionCount(minCount: number, maxCount?: number): RecentQuestionModel[] {
    return this.items.filter((question) => {
      if (maxCount !== undefined) {
        return question.execution_count >= minCount && question.execution_count <= maxCount;
      }
      return question.execution_count >= minCount;
    });
  }

  /**
   * Search local items by question text
   */
  public searchItems(searchText: string): RecentQuestionModel[] {
    const lowerSearchText = searchText.toLowerCase();
    return this.items.filter((question) =>
      question.question_text.toLowerCase().includes(lowerSearchText) ||
      (question.metadata?.tags || []).some((tag: string) => tag.toLowerCase().includes(lowerSearchText)),
    );
  }

  /**
   * Get local items sorted by execution count
   */
  public getSortedByExecutionCount(descending: boolean = true): RecentQuestionModel[] {
    return [...this.items].sort((a, b) =>
      descending ? b.execution_count - a.execution_count : a.execution_count - b.execution_count,
    );
  }

  /**
   * Get local items sorted by last executed date
   */
  public getSortedByLastExecuted(descending: boolean = true): RecentQuestionModel[] {
    return [...this.items].sort((a, b) => {
      const dateA = new Date(a.last_executed_at || '1970-01-01').getTime();
      const dateB = new Date(b.last_executed_at || '1970-01-01').getTime();
      return descending ? dateB - dateA : dateA - dateB;
    });
  }

  /**
   * Get statistics about loaded questions
   */
  public getLocalStats(): {
    total: number;
    byCategory: Record<string, number>;
    byPriority: Record<string, number>;
    averageExecutionCount: number;
    mostExecuted: RecentQuestionModel | null;
  } {
    const byCategory: Record<string, number> = {};
    const byPriority: Record<string, number> = {};
    let totalExecutions = 0;
    let mostExecuted: RecentQuestionModel | null = null;

    for (const question of this.items) {
      // Count by category
      const category = (question as any).getCategoryFromMetadata();
      byCategory[category] = (byCategory[category] || 0) + 1;

      // Count by priority - not available in recent questions
      // Recent questions don't have priority in the backend schema

      // Track executions
      totalExecutions += question.execution_count;

      // Track most executed
      if (!mostExecuted || question.execution_count > mostExecuted.execution_count) {
        mostExecuted = question;
      }
    }

    return {
      total: this.items.length,
      byCategory,
      byPriority,
      averageExecutionCount: this.items.length > 0 ? totalExecutions / this.items.length : 0,
      mostExecuted,
    };
  }

  /**
   * Check if collection has more pages available
   */
  public hasMorePages(): boolean {
    if (!this.pagination) { return false; }
    const { current_page, total_pages } = this.pagination;
    return current_page < total_pages;
  }

  /**
   * Get next page offset for pagination
   */
  public getNextPageOffset(): number {
    if (!this.pagination) { return 0; }
    const { current_page, page_size } = this.pagination;
    return current_page * page_size;
  }

  /**
   * Reset collection state
   */
  public reset(): void {
    this.items = [];
    this.total = 0;
    this.pagination = null;
  }
}
